import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../core/firebase";

type Post = {
  id: string;
  title?: string;
  author?: string;
  selftext?: string;
  selftext_html?: string;
  created?: number;
  tags?: string[];
  source?: string;
  isHidden?: boolean;
  isApproved?: boolean;
};

const formatDate = (createdSeconds?: number) => {
  if (!createdSeconds) return "";
  return new Date(createdSeconds * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const StoryScreen = () => {
  const { storyId } = useParams<{ storyId: string }>();
  const [story, setStory] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!storyId) return;
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, "posts", storyId));
        if (!snap.exists()) {
          setError("That story has slipped into the void.");
          setStory(null);
        } else {
          const data = snap.data() as any;
          if (
            data.isHidden ||
            (data.source === "user" && data.isApproved !== true)
          ) {
            setError("That story isn't available right now.");
            setStory(null);
          } else {
            setStory({ id: snap.id, ...data });
          }
        }
      } catch (err: any) {
        setError(err?.message ?? "Failed to load story.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storyId]);

  if (loading) {
    return (
      <div className="text-center py-20 text-ink-400">Loading…</div>
    );
  }

  if (error || !story) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <Link
          to="/"
          className="text-sm text-ink-400 hover:text-white"
        >
          &larr; Back
        </Link>
        <p className="mt-6 text-ink-300">
          {error || "Story not found."}
        </p>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto">
      <Link
        to="/"
        className="text-sm text-ink-400 hover:text-white"
      >
        &larr; Back to stories
      </Link>

      <header className="mt-6 mb-8">
        <h1 className="font-display text-4xl md:text-5xl text-white leading-tight">
          {story.title || "(untitled)"}
        </h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-sm text-ink-400">
          <span>by {story.author || "anonymous"}</span>
          {story.created && (
            <>
              <span aria-hidden>·</span>
              <span>{formatDate(story.created)}</span>
            </>
          )}
          {story.tags && story.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {story.tags.map((t) => (
                <span
                  key={t}
                  className="text-[11px] uppercase tracking-wide text-ink-300 bg-ink-700/60 px-2 py-0.5 rounded"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {story.selftext_html ? (
        <div
          className="story-body"
          // selftext_html is rendered for the public reader. If you ever
          // accept arbitrary HTML from end users, sanitize before saving.
          dangerouslySetInnerHTML={{ __html: story.selftext_html }}
        />
      ) : (
        <div className="story-body">
          {(story.selftext || "(no content)")
            .split(/\n+/)
            .map((para, i) => (
              <p key={i}>{para}</p>
            ))}
        </div>
      )}

      <hr className="border-ink-700 my-12" />
      <div className="flex items-center justify-between text-sm text-ink-400">
        <Link to="/" className="hover:text-white">
          ← More stories
        </Link>
        <Link
          to="/submit"
          className="px-4 py-2 rounded-md bg-accent-500 hover:bg-accent-400 text-white"
        >
          Share your own
        </Link>
      </div>
    </article>
  );
};

export default StoryScreen;
